// Single-device / logged-in-elsewhere enforcement for the staff softphone.
//
// Faithful port of the old frontend convention (qr-phone-frontend
// Login.jsx + loggedindevice.component.tsx, v3.2.0.0): the backend already
// pins a staff (role 3) session to the latest login_token (authenticate returns
// 401 action:logoutPage when an older device's token is presented), and each
// login overwrites the user's push token — so logging in here naturally
// displaces the old frontend. This module reproduces the client-owned
// `loggedindevice/<user_id>/<deviceId>` Realtime-Database tree that drives the
// real-time "you were signed out because you signed in elsewhere" eviction.
//
// IMPORTANT: the RTDB path and node shape MUST match the old frontend and the
// mobile app exactly (they all read/write the same tree) — do not rename.
import { get, ref, update, remove, child, onValue, off } from 'firebase/database'
import { database } from '../core/firebase'

const basePath = (userId: number | string) => `loggedindevice/${userId}`
const nowIso = () => new Date().toISOString()

// DEVICES OTHER THAN THIS ONE THAT ARE CURRENTLY MARKED AS LOGGED IN.
export const getOtherActiveDevices = async (
  userId: number | string,
  deviceId: string
): Promise<string[]> => {
  try {
    const snap = await get(ref(database, basePath(userId)))
    if (!snap.exists()) return []
    const data = snap.val() || {}
    return Object.keys(data).filter(
      (d) => d !== deviceId && data[d] && data[d].isLoggedIn === true
    )
  } catch (e) {
    // FAIL-OPEN: never block a legitimate login because the presence read failed.
    console.log('device session: could not read other devices', e)
    return []
  }
}

// FLIP EVERY OTHER DEVICE'S FLAG TO false — its watcher (subscribeDeviceSession)
// reacts and signs that device out in real time.
export const forceLogoutOtherDevices = async (
  userId: number | string,
  deviceId: string
): Promise<void> => {
  try {
    const baseRef = ref(database, basePath(userId))
    const snap = await get(baseRef)
    if (!snap.exists()) return
    const data = snap.val() || {}
    for (const d of Object.keys(data)) {
      if (d !== deviceId && data[d] && data[d].isLoggedIn) {
        await update(child(baseRef, d), {
          isLoggedIn: false,
          loggedOutAt: nowIso(),
          loggedOutReason: 'forced_logout_other_device',
        })
      }
    }
  } catch (e) {
    console.log('device session: could not force-logout other devices', e)
  }
}

// MARK THIS DEVICE AS LOGGED IN (written on every successful login) + prune stale
// entries so the tree does not accumulate.
export const markDeviceLoggedIn = async (
  userId: number | string,
  deviceId: string
): Promise<void> => {
  try {
    await update(ref(database, `${basePath(userId)}/${deviceId}`), {
      isLoggedIn: true,
      lastupdated: nowIso(),
      platform: 'web',
      loginTime: nowIso(),
      userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || 'unknown',
      ipAddress: 'web',
    })
    await cleanupStaleDevices(userId, deviceId)
  } catch (e) {
    console.log('device session: could not mark device logged in', e)
  }
}

// MARK THIS DEVICE AS LOGGED OUT (manual sign-out).
export const markDeviceLoggedOut = async (
  userId: number | string,
  deviceId: string
): Promise<void> => {
  try {
    await update(ref(database, `${basePath(userId)}/${deviceId}`), {
      isLoggedIn: false,
      loggedOutAt: nowIso(),
      loggedOutReason: 'manual_logout',
    })
  } catch (e) {
    console.log('device session: could not mark device logged out', e)
  }
}

// REMOVE DEVICE NODES NOT UPDATED IN THE LAST 7 DAYS OR ALREADY LOGGED OUT.
const cleanupStaleDevices = async (
  userId: number | string,
  currentDeviceId: string
): Promise<void> => {
  try {
    const baseRef = ref(database, basePath(userId))
    const snap = await get(baseRef)
    if (!snap.exists()) return
    const data = snap.val() || {}
    const now = Date.now()
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
    for (const d of Object.keys(data)) {
      if (d === currentDeviceId) continue
      const device = data[d]
      const last = device && device.lastupdated ? Date.parse(device.lastupdated) : NaN
      const stale = !isNaN(last) && now - last > sevenDaysMs
      if (stale || (device && device.isLoggedIn === false)) {
        await remove(child(baseRef, d))
      }
    }
  } catch (e) {
    console.log('device session: cleanup failed', e)
  }
}

// REAL-TIME WATCHER: invokes onEvicted() when this device's node is explicitly
// marked isLoggedIn:false (another device signed us out). Returns an unsubscribe.
// NB deliberately does NOT evict on a MISSING node (unlike the old frontend): a
// transient read/first-boot gap must not sign a working agent out — the backend
// login_token 401 (action:logoutPage, handled in core/http) is the hard backstop.
export const subscribeDeviceSession = (
  userId: number | string,
  deviceId: string,
  onEvicted: () => void
): (() => void) => {
  const r = ref(database, `${basePath(userId)}/${deviceId}`)
  const cb = (snapshot: any) => {
    if (snapshot.exists() && snapshot.val() && snapshot.val().isLoggedIn === false) {
      onEvicted()
    }
  }
  onValue(r, cb)
  return () => off(r, 'value', cb)
}
